import {get} from 'lodash';
import {parseQueryPath} from '../utils/QueryPathUtils';
import {memoize, weakMemo} from '@packages/utilities/memo';
import {
  qualify,
  namesEqual,
  identifier,
  Qualified,
} from '@packages/systems/wfdl-Qualified';
import {
  Plugins,
  Component,
  EvalEnv,
  Expressions,
  matchExpression,
  Variable,
  Types,
  VariableMap,
} from '@packages/systems/wfdl';
import {
  getPageBindingContextNode,
  type BindingContextGraphNode,
} from '@packages/systems/dynamo/gateway';

import {
  type ExpressionElement,
  type Expression,
  ERecord,
} from '@packages/systems/wfdl-ast';
import {
  QueryContext as QueryContextInterface,
  QueryVariables,
  QueryPath,
  PluginArguments,
  RawData,
  DataDependentFieldDef,
  BindingPath,
  ExpressionEvaluationContext as ExpressionEvaluationContextInterface,
  ExpressionEvaluationResult,
} from '../types';
import type {PageRecordType} from '@packages/systems/core/records/PageRecord';
import type {RuntimeInfo} from '@packages/systems/dynamo/source-types';
import {concat, constant, emptyArray} from '@packages/utilities/fp/utils';
import {getDataTypeFromElement} from '@packages/systems/user-components/utils';
import {getMd5Hash} from '@packages/utilities/md5-browser';
import {
  getGuidNullable,
  NativeId,
} from '@packages/systems/core/utils/NativeIdUtils';
import {getInstanceKey} from '../utils/getInstanceKey';
import {setDynamoEnv} from '../dynamo-env';
import {getDynamoBindingPaths} from '@packages/systems/dynamo/wfdl';
import {getInstancesFromRendererNativeIdPath} from '../utils/getInstancesFromRendererNativeIdPath';
import {pageComponentType} from '@packages/utilities/platform';

const documentType = qualify(Plugins.name('Ssr'), Component.name('Document'));

function _getQueryPath(
  bindingContextGraphNode: BindingContextGraphNode,
  path: BindingPath
): QueryPath | null {
  if (path.length === 1 && path[0] === '_this') {
    return ['id'];
  }

  const bindingSourceIndex = bindingContextGraphNode.getBindingSourceIndex();

  if (bindingSourceIndex) {
    const bindingSource = bindingSourceIndex.getBindingSource(path);

    if (bindingSource && bindingSource.queryPath.length > 0) {
      return bindingSource.queryPath;
    }
  }

  return null;
}
export const createPageInitialContext = memoize(
  (
    rootElement: ExpressionElement,
    page: PageRecordType,
    variables: QueryVariables,
    pluginArguments: PluginArguments,
    rootEnv: EvalEnv.EvalEnvType,
    baseRendererNativeIdPath: NativeId[],
    focusedSymbolNativeIdPath: NativeId[],
    rootComponentType: Qualified<Component.ComponentName>,
    componentMap: Component.ComponentMap,
    plugins: Plugins.PluginsType
  ): QueryContextInterface => {
    const {collections} = pluginArguments;
    const bindingContextGraphNode = getPageBindingContextNode(
      page,
      collections,
      pluginArguments.runtimeEnvironment
    );

    const runtimeInfo: RuntimeInfo = {
      environment: pluginArguments.runtimeEnvironment,
      cmsLocaleId: pluginArguments.cmsLocaleId,
      paginationPageNumbers: pluginArguments.paginationPageNumbers,
      publicationId: pluginArguments.publicationId,
      showSampleSearchResults: pluginArguments.showSampleSearchResults,
      timezone: pluginArguments.timezone,
    };

    const exprEvaluationContext = new ExpressionEvaluationContext({
      rootEnv,
      expandedInstances: getInstancesFromRendererNativeIdPath(
        baseRendererNativeIdPath,
        rootComponentType,
        componentMap
      ),
      focusedSymbolNativeIdPath,
      rootComponentType,
      componentMap,
      plugins,
    });

    const querySpecifications = bindingContextGraphNode.getQuerySpecifications(
      runtimeInfo,
      exprEvaluationContext
    );

    const queryPathPrefix: QueryPath =
      querySpecifications.length === 1 &&
      querySpecifications[0] &&
      querySpecifications[0].action === 'traversed'
        ? querySpecifications[0].queryPathPrefix
        : [];

    const descendantQueryPaths: {[elId: string]: QueryPath[]} = {};

    // Always query for the page item of collection template page in SSR.
    // This is necessary so that createSsrDynamoDataProvider can throw a
    // "No dynamic item" error if the page item cannot be found, and so that
    // root-level bindings that depend on the CMS item name can resolve from
    // the same query without requiring a separate fetch.
    if (
      queryPathPrefix.length > 0 &&
      namesEqual(rootElement.type, documentType)
    ) {
      const pageItemQueryPaths = [queryPathPrefix];
      const nameQueryPath = _getQueryPath(bindingContextGraphNode, ['name']);
      if (nameQueryPath) {
        pageItemQueryPaths.push(queryPathPrefix.concat(nameQueryPath));
      }
      descendantQueryPaths[rootElement.id] = pageItemQueryPaths;
    }

    return new QueryContext({
      queryPathPrefix,
      descendantQueryPaths,
      bindingContextGraphNode,
      runtimeInfo,
      exprEvaluationContext,
    });
  }
);

type DescendantQueryPaths = QueryContextInterface['descendantQueryPaths'];

class QueryContext implements QueryContextInterface {
  internalQueryPathPrefix: QueryPath;
  hideQueryPathPrefix: boolean;
  descendantQueryPaths: DescendantQueryPaths;
  bindingContextGraphNode: BindingContextGraphNode;
  runtimeInfo: RuntimeInfo;
  exprEvaluationContext: ExpressionEvaluationContextInterface;

  constructor({
    queryPathPrefix,
    hideQueryPathPrefix,
    descendantQueryPaths,
    bindingContextGraphNode,
    runtimeInfo,
    exprEvaluationContext,
  }: {
    queryPathPrefix: QueryPath;
    hideQueryPathPrefix?: boolean;
    descendantQueryPaths: DescendantQueryPaths;
    bindingContextGraphNode: BindingContextGraphNode;
    runtimeInfo: RuntimeInfo;
    exprEvaluationContext: ExpressionEvaluationContextInterface;
  }) {
    this.internalQueryPathPrefix = queryPathPrefix;
    this.descendantQueryPaths = descendantQueryPaths;
    this.bindingContextGraphNode = bindingContextGraphNode;
    this.runtimeInfo = runtimeInfo;
    this.hideQueryPathPrefix = hideQueryPathPrefix ?? false;
    this.exprEvaluationContext = exprEvaluationContext;
  }

  get queryPathPrefix(): QueryPath {
    return this.hideQueryPathPrefix ? [] : this.internalQueryPathPrefix;
  }

  get instanceKey(): string {
    return this.exprEvaluationContext.instanceKey;
  }

  createContextForComponent = weakMemo(
    (instance: ExpressionElement): QueryContextInterface | null => {
      const nextExprEvaluationContext =
        this.exprEvaluationContext.addExpandedInstance(instance);

      if (nextExprEvaluationContext === null) {
        // short circuit for infinite loops from cyclic components
        return null;
      }

      if (nextExprEvaluationContext === this.exprEvaluationContext) {
        return this;
      }

      return new QueryContext({
        queryPathPrefix: this.internalQueryPathPrefix,
        descendantQueryPaths: this.descendantQueryPaths,
        bindingContextGraphNode: this.bindingContextGraphNode,
        runtimeInfo: this.runtimeInfo,
        hideQueryPathPrefix: this.hideQueryPathPrefix,
        exprEvaluationContext: nextExprEvaluationContext,
      });
    }
  );

  update = weakMemo((element: ExpressionElement): QueryContextInterface => {
    const bindingContextGraphNode =
      this.bindingContextGraphNode.getNextNode(element);

    if (bindingContextGraphNode === null) {
      return EMPTY_QUERY_CONTEXT;
    }

    if (bindingContextGraphNode === this.bindingContextGraphNode) {
      return this;
    }

    const querySpecifications = bindingContextGraphNode.getQuerySpecifications(
      this.runtimeInfo,
      this.exprEvaluationContext
    );

    let nextQueryPathPrefix;
    let nextHideQueryPathPrefix;
    const nextQueryPaths: QueryPath[] = [];

    for (const querySpecification of querySpecifications) {
      if (querySpecification.action === 'deferred') {
        for (const queryPath of querySpecification.queryPaths) {
          nextQueryPaths.push(
            this.internalQueryPathPrefix.concat(
              this.wrapQueryPathWithDataProvider(queryPath)
            )
          );
        }
      }

      if (querySpecification.action === 'traversed') {
        const {queryPathPrefix, queryPaths, relative, hideQueryPathPrefix} =
          querySpecification;
        const wrappedQueryPathPrefix =
          this.wrapQueryPathWithDataProvider(queryPathPrefix);

        nextHideQueryPathPrefix = hideQueryPathPrefix;

        nextQueryPathPrefix = relative
          ? this.internalQueryPathPrefix.concat(wrappedQueryPathPrefix)
          : wrappedQueryPathPrefix;

        if (queryPaths) {
          for (const queryPath of queryPaths) {
            const wrappedQueryPath =
              this.wrapQueryPathWithDataProvider(queryPath);
            nextQueryPaths.push(
              relative
                ? this.internalQueryPathPrefix.concat(wrappedQueryPath)
                : wrappedQueryPath
            );
          }
        }
      }
    }

    let nextDescendantQueryPaths;

    if (nextQueryPaths.length > 0) {
      nextDescendantQueryPaths = {
        ...this.descendantQueryPaths,
        [element.id]: nextQueryPaths,
      };
    }

    return new QueryContext({
      queryPathPrefix: nextQueryPathPrefix ?? this.internalQueryPathPrefix,
      descendantQueryPaths:
        nextDescendantQueryPaths ?? this.descendantQueryPaths,
      bindingContextGraphNode,
      runtimeInfo: this.runtimeInfo,
      hideQueryPathPrefix: nextHideQueryPathPrefix ?? this.hideQueryPathPrefix,
      exprEvaluationContext: this.exprEvaluationContext,
    });
  });

  getQueryPath(bindingPath: BindingPath): QueryPath | null {
    /*
      A binding path of ['_this'] is a special case used in CMS collection
      binding contexts to provide a "Current Item" binding source for
      conditionals.

      The long-term solution should be to implement the "Current Item" binding
      source as a bindable property in the CMS gateway instead of special cases
      like the one here.
    */
    return _getQueryPath(this.bindingContextGraphNode, bindingPath);
  }

  /*
    Wraps data-dependent field definitions in query paths with a function that
    ensures the input is scoped to a slice of response data that corresponds
    with the surrounding query context.

    This trick ensures that query specifications from data source definitions
    don't need to be concerned with finding the correct slice of response data
    in data-dependent field definition functions.

    In practice, this will only ever affect Collection Lists with DYN_CONTEXT
    filters that create both level 0 and level 1 queries.
  */
  wrapQueryPathWithDataProvider(queryPath: QueryPath): QueryPath {
    if (queryPath.some((fieldDef) => typeof fieldDef === 'function')) {
      return queryPath.map((fieldDef) =>
        typeof fieldDef === 'function'
          ? this.wrapFieldDefWithDataProvider(
              fieldDef,
              this.internalQueryPathPrefix
            )
          : fieldDef
      );
    }
    return queryPath;
  }

  wrapFieldDefWithDataProvider(
    fieldDef: DataDependentFieldDef,
    queryPathPrefix: QueryPath
  ): DataDependentFieldDef {
    return (data: RawData): string => {
      const path = parseQueryPath(data)(queryPathPrefix);
      const rawDataSlice = get(data, path);
      /*
        !!! HACK ALERT !!!

       Ensure that expression evaluation in query specifiers can handle
       Dynamo universal bindings. This is the collection list props equivalent
       to calling fieldDef with rawDataSlice as argument to handle level 2
       queries.

       This hack works by mutating the rootEnv property of the current
       this.exprEvaluationContext object. The same object has been passed
       to the query specifier that produced the fieldDef function we call
       right after, which means the query specifier now can magically evaluate
       expressions with bindings to Dynamo data fetched by the level 1 query.
      */
      this.exprEvaluationContext.updateDynamoEnv(rawDataSlice);
      return fieldDef(rawDataSlice);
    };
  }
}

const EMPTY_QUERY_CONTEXT: QueryContextInterface = {
  queryPathPrefix: [],
  descendantQueryPaths: {},
  instanceKey: '',
  createContextForComponent: (_instance: ExpressionElement) =>
    EMPTY_QUERY_CONTEXT,
  update: (_element: ExpressionElement) => EMPTY_QUERY_CONTEXT,
  getQueryPath: (_bindingPath: BindingPath) => null,
};

const dataVar = Variable.unqualified(identifier('data'));

class ExpressionEvaluationContext implements ExpressionEvaluationContextInterface {
  rootEnv: EvalEnv.EvalEnvType;
  expandedInstances: ExpressionElement[];
  focusedSymbolNativeIdPath: NativeId[];
  rootComponentType: Qualified<Component.ComponentName>;
  componentMap: Component.ComponentMap;
  plugins: Plugins.PluginsType;

  // private variables to memoize the calculation of instance keys and hashes
  #instanceKey?: string;
  #instanceHash?: string | null;
  #instanceIdPath?: string[];

  constructor({
    rootEnv,
    expandedInstances,
    focusedSymbolNativeIdPath,
    rootComponentType,
    componentMap,
    plugins,
  }: {
    rootEnv: EvalEnv.EvalEnvType;
    expandedInstances?: ExpressionElement[];
    focusedSymbolNativeIdPath: NativeId[];
    rootComponentType: Qualified<Component.ComponentName>;
    componentMap: Component.ComponentMap;
    plugins: Plugins.PluginsType;
  }) {
    this.rootEnv = rootEnv;
    this.expandedInstances = expandedInstances ?? [];
    this.focusedSymbolNativeIdPath = focusedSymbolNativeIdPath;
    this.rootComponentType = rootComponentType;
    this.componentMap = componentMap;
    this.plugins = plugins;
  }

  get instanceKey(): string {
    if (this.#instanceKey !== undefined) {
      return this.#instanceKey;
    }
    const instanceKey = getInstanceKey(this.instanceIdPath);
    this.#instanceKey = instanceKey;
    return instanceKey;
  }

  get instanceHash(): string | null {
    if (this.#instanceHash !== undefined) {
      return this.#instanceHash;
    }
    let instanceHash: string | null = null;
    if (this.instanceKey.length > 0) {
      instanceHash = getMd5Hash(this.instanceKey);
    }
    this.#instanceHash = instanceHash;
    return instanceHash;
  }

  get instanceIdPath(): string[] {
    if (this.#instanceIdPath !== undefined) {
      return this.#instanceIdPath;
    }
    const instanceIdPath = this.expandedInstances.map((inst) => inst.id);
    this.#instanceIdPath = instanceIdPath;
    return instanceIdPath;
  }

  addExpandedInstance(
    instance: ExpressionElement
  ): ExpressionEvaluationContextInterface | null {
    if (
      this.expandedInstances.some((seenInst) =>
        namesEqual(seenInst.type, instance.type)
      )
    ) {
      // short circuit if we have seen this component type in the stack to
      // prevent infinite loops from cyclic components
      return null;
    }

    return new ExpressionEvaluationContext({
      rootEnv: this.rootEnv,
      expandedInstances: [...this.expandedInstances, instance],
      focusedSymbolNativeIdPath: this.focusedSymbolNativeIdPath,
      rootComponentType: this.rootComponentType,
      componentMap: this.componentMap,
      plugins: this.plugins,
    });
  }

  /*
    Evaluate `expr` using `EvalEnv` from renderer augmented with
    an approximation of component props data. Returns a result object that
    indicates whether evaluation of the given Expression is dependent on
    data from a previous query level or not.
  */
  evaluateExpr(expr: Expression): ExpressionEvaluationResult {
    let dataBindingKeysInScope = getDataBindingKeys(expr);
    let isDataDependent: boolean = isDynamoBound(expr);
    const isComponentScopeDependent = dataBindingKeysInScope.length > 0;

    const scopedDatas: Expression[] = [];
    const defaultValues: ScopedComponentDefaultValues[] = [];

    const focusedComponentDivergenceIndex =
      getIndexOfLastComponentInstanceInFocusedComponentPath(
        this.focusedSymbolNativeIdPath,
        this.expandedInstances
      );

    // traverse the stack of expanded component instances to build up a
    // minimal slice of component instance element data needed to evaluate
    // component prop bindings found in `expr`
    for (let i = this.expandedInstances.length - 1; i >= 0; i--) {
      if (dataBindingKeysInScope.length === 0) {
        // no component prop bindings were found at the end of the previous
        // iteration, i.e. we have reached the depth required to evaluate all
        // component prop bindings found recursively from the original `expr`
        break;
      }

      const instance = this.expandedInstances[i];
      if (!instance) {
        // ostensibly this check is here just to satisfy TS
        break;
      }

      const scopedDataRecord: {[key: string]: Expression} = {};
      const scopedDefaultValues: ScopedComponentDefaultValues = {};
      const dataType = getDataTypeFromElement(this.plugins)(this.componentMap)(
        instance
      );
      const overridesFields = Types.getRecordFields(dataType);
      if (!overridesFields) {
        break;
      }
      const isInFocusedComponentInstance = focusedComponentDivergenceIndex >= i;
      for (const key of dataBindingKeysInScope) {
        const val = Expressions.getIn(Expressions.getElementData(instance), [
          {in: 'Record', at: key},
        ]);
        if (
          val &&
          // use only default values for focused component instances
          !isInFocusedComponentInstance
        ) {
          scopedDataRecord[key] = val;
        } else {
          const type = overridesFields[key];
          if (!type) {
            break;
          }
          const defaultValue = Types.getTypeDefaultValue(this.plugins)(type);
          scopedDefaultValues[key] = defaultValue;
        }
      }

      const scopedData = ERecord(scopedDataRecord);
      scopedDatas.unshift(scopedData);
      defaultValues.unshift(scopedDefaultValues);

      dataBindingKeysInScope = getDataBindingKeys(scopedData);
      if (!isDataDependent) {
        // once isDataDependent is true, we don't want to flip it back to false
        isDataDependent = isDynamoBound(scopedData);
      }
    }

    if (
      !namesEqual(this.rootComponentType, pageComponentType) &&
      dataBindingKeysInScope.length > 0
    ) {
      const rootComponent = this.componentMap.get(this.rootComponentType);
      const rootFields = rootComponent
        ? Types.getRecordFields(rootComponent.dataType)
        : null;
      if (rootFields) {
        const rootScopedDefaultValues: ScopedComponentDefaultValues = {};
        for (const key of dataBindingKeysInScope) {
          const type = rootFields[key];
          if (!type) {
            break;
          }
          rootScopedDefaultValues[key] = Types.getTypeDefaultValue(
            this.plugins
          )(type);
        }
        defaultValues.push(rootScopedDefaultValues);
      }
    }

    if (isDataDependent) {
      // Evaluation of `expr` is dependent on data from a preceding query level.
      // We indicate that by returning a 'dynamic' result with a value callback.
      // Consumers are responsible for propagating this callback to QueryPaths.
      return {
        type: 'dynamic',
        needsComponentData: isComponentScopeDependent,
        valueCallback: () => {
          return evaluateExpressionWithNarrowScope(
            expr,
            this.rootEnv,
            scopedDatas,
            defaultValues
          );
        },
      };
    }

    // Evaluation of `expr` is not data dependent so we return a value directly.
    return {
      type: 'static',
      needsComponentData: isComponentScopeDependent,
      value: evaluateExpressionWithNarrowScope(
        expr,
        this.rootEnv,
        scopedDatas,
        defaultValues
      ),
    };
  }

  inspectExpr(expr: Expression, inspector: (expr: Expression) => void) {
    inspector(expr);

    let dataBindingKeysInScope = getDataBindingKeys(expr);
    // traverse the stack of expanded component instances to so we can inspect
    // the minimal slice of component instance element data that makes up the
    // eventual evaluated value of the given expression scoped to component
    // props bindings
    for (let i = this.expandedInstances.length - 1; i >= 0; i--) {
      if (dataBindingKeysInScope.length === 0) {
        // no component prop bindings were found at the end of the previous
        // iteration, i.e. we have reached the depth required to inspect all
        // component prop bindings found recursively from the original `expr`
        break;
      }

      const instance = this.expandedInstances[i];
      if (!instance) {
        // ostensibly this check is here just to satisfy TS
        break;
      }

      const nextDataBindingKeysInScope: string[] = [];

      for (const key of dataBindingKeysInScope) {
        const val = Expressions.getIn(Expressions.getElementData(instance), [
          {in: 'Record', at: key},
        ]);
        if (val) {
          inspector(val);
          nextDataBindingKeysInScope.push(...getDataBindingKeys(val));
        }
      }

      dataBindingKeysInScope = nextDataBindingKeysInScope;
    }
  }

  updateDynamoEnv(rawDataSlice: any) {
    // we mutate rootEnv to provide a magical experience to query specifiers
    this.rootEnv = setDynamoEnv(
      this.rootEnv,
      rawDataSlice,
      // dynContextData is not relevant for this use case since we are not
      // evaluating DYN_CONTEXT bindings
      null
    );
  }
}

type ScopedComponentDefaultValues = {[propId: string]: unknown};

function evaluateExpressionWithNarrowScope(
  expr: Expression,
  rootEnv: EvalEnv.EvalEnvType,
  scopedDatas: Expression[],
  defaultValues: ScopedComponentDefaultValues[]
): unknown {
  // Insert all the default values to the eval env.
  // If there are no default values then keep current rootEnv.
  // This is required for the Designer where we have the correct data already in the rootEnv thus we don't want to override it.
  const evalEnvWithDefaults =
    defaultValues.length > 0
      ? EvalEnv.insertValue(
          rootEnv,
          dataVar,
          Object.assign({}, ...defaultValues)
        )
      : rootEnv;
  // build an EvalEnv instance that should closely resemble the EvalEnv
  // we build when expanding components in the renderer but scoped
  // to contain just the component prop data required to evaluate `expr`
  const evalEnv = scopedDatas.reduce((env, data) => {
    const evaluatedOverrides = Expressions.evaluate(env, data);
    const envDefaultValues =
      VariableMap.get(EvalEnv.getValues(env), dataVar) ?? {};
    return EvalEnv.insertValue(env, dataVar, {
      ...envDefaultValues,
      ...evaluatedOverrides,
    });
  }, evalEnvWithDefaults);

  return Expressions.evaluate(evalEnv, expr);
}

const getDataBindingKeys: (arg1: Expression) => string[] =
  Expressions.everything(concat)(
    matchExpression({
      Select: ({from, prop}) => {
        if (Expressions.isDataVariable(from)) {
          return Expressions.stop([prop]);
        }

        return Expressions.resume(emptyArray);
      },
      Element: constant(Expressions.stop(emptyArray)),
      _: constant(Expressions.resume(emptyArray)),
    })
  );

function isDynamoBound(expr: Expression): boolean {
  return getDynamoBindingPaths(expr).length > 0;
}

/**
 * Returns the index of the last instance in expandedInstances that is along the
 * focused component path.
 */
function getIndexOfLastComponentInstanceInFocusedComponentPath(
  focusedSymbolNativeIdPath: NativeId[],
  expandedInstances: ExpressionElement[]
) {
  let index = -1;

  for (let i = 0; i < expandedInstances.length; i++) {
    const instance = expandedInstances[i];
    if (instance?.id === getGuidNullable(focusedSymbolNativeIdPath[i])) {
      index = i;
    } else {
      break;
    }
  }

  return index;
}
